import { COGNITO_REGION, USER_POOL_CLIENT_ID, USER_POOL_ID, DEMO_CLIENT_ID } from './config';
import type { Session } from './types';

const KEY = 'leadsirl.session';
const COGNITO = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
/** Renewed a few minutes before the hour is up, so a request never leaves with a sign-in about to lapse. */
const EARLY_MS = 5 * 60_000;
/** Fired when this tab is signed out: the 30 days are up, the login was switched off, or someone signed out or in as another user. */
export const SIGNED_OUT = 'leadsirl:signed-out';
/** What Cognito says when the 30-day sign-in itself is no good any more. Anything else (busy, down) is tried again later. */
const REJECTED = ['NotAuthorizedException', 'UserNotFoundException', 'PasswordResetRequiredException', 'UserNotConfirmedException'];

function parseJwt(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

function claim(token: string, name: string): unknown {
  try {
    return parseJwt(token)[name];
  } catch {
    return undefined;
  }
}

const expiresAt = (token: string) => Number(claim(token, 'exp') ?? 0) * 1000;
/** How far this computer's clock is behind Cognito's, from when the pass was made. */
const skewOf = (token: string) => {
  const iat = Number(claim(token, 'iat') ?? 0) * 1000;
  return iat ? iat - Date.now() : 0;
};
/** Now, by Cognito's clock, so a computer whose clock is off still renews at the right time. */
const nowFor = (s: Session) => Date.now() + (s.skewMs ?? 0);

/** The same person signed in (the user id inside the pass; the email for an older pass). */
export function sameUser(a: Session, b: Session): boolean {
  const ua = String(claim(a.idToken, 'sub') ?? a.email);
  const ub = String(claim(b.idToken, 'sub') ?? b.email);
  return ua === ub;
}

function stored(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

/**
 * The saved sign-in. It lasts 30 days on this device: the one-hour pass inside it is renewed as
 * needed. One saved before renewal existed has nothing to renew with, so the old hour still applies.
 */
export function loadSession(): Session | null {
  const s = stored();
  if (!s?.idToken) return null;
  if (!s.refreshToken && nowFor(s) > expiresAt(s.idToken) - 30_000) {
    clearSession();
    return null;
  }
  return s;
}

export function saveSession(s: Session) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession() {
  localStorage.removeItem(KEY);
}

/** This tab goes back to the sign-in screen. The saved sign-in is cleared only when it is this tab's own. */
function leave(clear: boolean): Error {
  if (clear) clearSession();
  window.dispatchEvent(new Event(SIGNED_OUT));
  return new Error('Your sign-in ran out. Sign in again.');
}

/**
 * This tab's sign-in as saved now (it may have been renewed by another tab). Signed out, or
 * signed in as someone else in another tab: this tab is signed out too, and never borrows theirs.
 */
function current(tab: Session): Session {
  const s = stored();
  if (!s) throw leave(false);
  if (!sameUser(s, tab)) throw leave(false);
  return s;
}

async function cognito(target: string, body: Record<string, unknown>): Promise<{ res: Response; data: Record<string, unknown> }> {
  const res = await fetch(COGNITO, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { res, data };
}

let renewing: Promise<string> | null = null;

async function renew(s: Session): Promise<string> {
  if (!s.refreshToken) throw leave(true);
  let out: Awaited<ReturnType<typeof cognito>>;
  try {
    out = await cognito('InitiateAuth', { AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: USER_POOL_CLIENT_ID, AuthParameters: { REFRESH_TOKEN: s.refreshToken } });
  } catch {
    // No connection: stay signed in, the next request tries again.
    throw new Error("Can't reach the server. Check your connection and try again.");
  }
  const r = out.data.AuthenticationResult as Record<string, string> | undefined;
  if (!out.res.ok || !r?.IdToken) {
    // The 30 days are up, or the login was switched off: back to sign-in. Busy or down: try again later.
    const type = String(out.data.__type ?? '').split('#').pop() ?? '';
    if (REJECTED.includes(type)) {
      const now = stored();
      throw leave(!!now && sameUser(now, s));
    }
    throw new Error('Could not renew your sign-in. Try again in a moment.');
  }
  // Signed out, or signed in as someone else, while this was on its way: nothing is saved.
  const now = stored();
  if (!now || !sameUser(now, s)) throw leave(false);
  const next: Session = {
    ...now, idToken: r.IdToken, accessToken: r.AccessToken ?? now.accessToken, skewMs: skewOf(r.IdToken),
    ...(r.RefreshToken ? { refreshToken: r.RefreshToken } : {}),
  };
  saveSession(next);
  return next.idToken;
}

/** The sign-in to send with a request from this tab, renewed quietly when it is close to running out. */
export async function freshIdToken(tab: Session): Promise<string> {
  const s = current(tab);
  if (nowFor(s) < expiresAt(s.idToken) - EARLY_MS) return s.idToken;
  renewing ??= renew(s).finally(() => { renewing = null; });
  return renewing;
}

/**
 * The server turned down `used` (it lapsed early, or this computer's clock is off): renew it now.
 * When another request already renewed it meanwhile, that newer one is used instead.
 */
export async function renewNow(tab: Session, used: string): Promise<string> {
  const s = current(tab);
  if (s.idToken !== used && nowFor(s) < expiresAt(s.idToken) - EARLY_MS) return s.idToken;
  renewing ??= renew(s).finally(() => { renewing = null; });
  return renewing;
}

/**
 * Another tab signed out, or signed in as someone else: this tab follows straight away rather than
 * at its next request. Returns the function that stops watching.
 */
export function watchOtherTabs(tab: Session, onSignedOut: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY && e.key !== null) return;
    const s = stored();
    if (!s || !sameUser(s, tab)) onSignedOut();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** What a sign-in step leads to: signed in, or one more screen first. */
export type SignInStep =
  | { session: Session }
  /** Invited: chooses their own password the first time (the emailed one is temporary). */
  | { choosePassword: true; email: string; cognitoSession: string }
  /** LeadsIRL reset their password: they set a new one with a code from their email. */
  | { resetPassword: true; email: string };

/** The message Cognito gives, minus its type prefix, in plain words where we know better. */
function plain(data: Record<string, unknown>, fallback: string): string {
  const type = String(data.__type ?? '').split('#').pop() ?? '';
  const msg = String(data.message ?? '').replace(/^\w+Exception:\s*/, '').trim();
  if (type === 'NotAuthorizedException') {
    if (/temporary password has expired/i.test(msg)) return 'Your invite has expired. Ask LeadsIRL to send you a new one.';
    if (/attempts exceeded/i.test(msg)) return 'Too many tries. Wait a few minutes, or use Forgot your password.';
    if (/cannot be reset in the current state/i.test(msg)) {
      return 'You have not signed in yet: use the temporary password from your invite email. If it has expired, ask LeadsIRL to send a new one.';
    }
    if (/disabled/i.test(msg)) return 'This login is switched off. Ask LeadsIRL if that is a mistake.';
    if (/incorrect username or password/i.test(msg)) return 'That email and password do not match.';
  }
  if (type === 'UserNotFoundException') return 'That email and password do not match.';
  if (type === 'CodeMismatchException') return 'That code is not right. Check the latest email and try again.';
  if (type === 'ExpiredCodeException') return 'That code has expired. Ask for a new one.';
  if (type === 'InvalidPasswordException') return 'Use at least 12 characters, with an upper-case letter, a lower-case letter and a number.';
  if (type === 'LimitExceededException' || type === 'TooManyRequestsException') return 'Too many tries. Wait a few minutes and try again.';
  return msg || fallback;
}

function sessionFrom(result: Record<string, string>, email: string): Session {
  const idToken = result.IdToken;
  const claims = parseJwt(idToken);
  const groups = (claims['cognito:groups'] as string[]) ?? [];
  const clientId = (claims['custom:clientId'] as string) || DEMO_CLIENT_ID;
  const session: Session = {
    idToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
    skewMs: skewOf(idToken),
    email: String(claims.email ?? email),
    groups,
    clientId,
  };
  saveSession(session);
  void USER_POOL_ID;
  return session;
}

/**
 * Logins are made with the email in small letters, and Cognito may tell "Mike@" from "mike@".
 * So the email is tried as typed, then in small letters. The step says which one worked.
 */
export async function signIn(typed: string, password: string): Promise<SignInStep> {
  const attempt = (email: string) => cognito('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: USER_POOL_CLIENT_ID,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });
  let email = typed;
  let { res, data } = await attempt(email);
  const typeOf = (d: Record<string, unknown>) => String(d.__type ?? '').split('#').pop() ?? '';
  if (typed !== typed.toLowerCase() && ['UserNotFoundException', 'NotAuthorizedException'].includes(typeOf(data))
    && /incorrect username or password|does not exist/i.test(String(data.message ?? ''))) {
    email = typed.toLowerCase();
    ({ res, data } = await attempt(email));
  }
  const type = typeOf(data);
  if (type === 'PasswordResetRequiredException') return { resetPassword: true, email };
  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED' && typeof data.Session === 'string') {
    return { choosePassword: true, email, cognitoSession: data.Session };
  }
  const result = data.AuthenticationResult as Record<string, string> | undefined;
  if (!res.ok || data.__type || !result) throw new Error(plain(data, 'Sign-in failed. Try again.'));
  return { session: sessionFrom(result, email) };
}

/** First sign-in after an invite: their own password replaces the emailed one, and they are in. */
export async function choosePassword(email: string, password: string, cognitoSession: string): Promise<Session> {
  const { res, data } = await cognito('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: USER_POOL_CLIENT_ID,
    Session: cognitoSession,
    ChallengeResponses: { USERNAME: email, NEW_PASSWORD: password },
  });
  const result = data.AuthenticationResult as Record<string, string> | undefined;
  if (!res.ok || data.__type || !result) throw new Error(plain(data, 'Could not save that password. Try again.'));
  return sessionFrom(result, email);
}

/** Forgot password: Cognito emails a code (and says nothing about whether the email has a login). */
export async function sendResetCode(email: string): Promise<void> {
  const { res, data } = await cognito('ForgotPassword', { ClientId: USER_POOL_CLIENT_ID, Username: email });
  if (!res.ok || data.__type) throw new Error(plain(data, 'Could not send a code. Try again.'));
}

/** The emailed code and a new password; then they sign in with it. */
export async function resetPassword(email: string, code: string, password: string): Promise<void> {
  const { res, data } = await cognito('ConfirmForgotPassword', {
    ClientId: USER_POOL_CLIENT_ID, Username: email, ConfirmationCode: code, Password: password,
  });
  if (!res.ok || data.__type) throw new Error(plain(data, 'Could not change the password. Try again.'));
}

/**
 * Sign this tab's user out on this device. Their 30-day renewal is also cancelled with Cognito,
 * so a copy left on a shared computer stops working (best effort: signing out never waits on it).
 * Another user's sign-in saved meanwhile by another tab is left alone.
 */
export function signOut(tab: Session) {
  const s = stored();
  const mine = !!s && sameUser(s, tab);
  if (mine || !s) clearSession();
  const token = (mine ? s?.refreshToken : undefined) ?? tab.refreshToken;
  if (token) void cognito('RevokeToken', { ClientId: USER_POOL_CLIENT_ID, Token: token }).catch(() => undefined);
}
